import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, Gift, Loader2, ShieldCheck, Sparkles, Trophy, WalletCards } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  claimVipReward,
  fetchVipRewardsStatus,
  resolveVipRewardImageUrl,
  vipRewardsCacheScopeForUser,
} from '../../services/vipRewards';

const FALLBACK_BANNER = '/assets/fifa-kit-home.png';

const formatFp = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return '0 FP';
  return `${Number.isInteger(amount) ? amount : amount.toFixed(2).replace(/\.?0+$/, '')} FP`;
};

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const formatCountdown = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds % 60).padStart(2, '0')}s`;
};

const claimAmount = (claim) => {
  if (claim?.type === 'cashback') return Number(claim.reward_cash ?? claim.reward_fp ?? 0);
  return Number(claim?.reward_fp || 0);
};

const claimLabel = (claim) => {
  const amount = claimAmount(claim);
  if (claim?.type === 'cashback') return `${formatMoney(amount)} wallet cashback`;
  return formatFp(amount);
};

export default function VipRewardsPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState('');
  const [cooldownEndsAt, setCooldownEndsAt] = useState(0);
  const [clockNow, setClockNow] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const requestVersion = useRef(0);

  const item = status?.item || {};
  const claims = status?.claims || [];
  const availableClaims = status?.available_claims || [];
  const progress = status?.cashback_progress || {};
  const tiers = item?.freeplay_offer?.tiers || [];
  const cashback = item?.cashback || {};
  const freeplayEnabled = item?.freeplay_offer?.enabled !== false;
  const cashbackEnabled = item?.cashback?.enabled !== false;
  // An uploaded flyer takes priority. The live cashback summary is only a fallback;
  // do not replace a tenant's artwork just because cashback is enabled.
  const bannerUrl = resolveVipRewardImageUrl(
    item.banner_image_url,
    cashbackEnabled ? '' : item.fallback_banner_url || FALLBACK_BANNER,
  );
  const cacheScope = vipRewardsCacheScopeForUser(user);
  const { tenantId, userId } = cacheScope;
  const requiredDeposits = Number(cashback.required_deposits ?? 6);
  const currentDeposits = Math.min(requiredDeposits, Number(progress.deposit_count || 0));
  const cooldownSeconds = Math.max(0, Math.ceil((cooldownEndsAt - clockNow) / 1000));
  const waitingForCashback = cashbackEnabled && cooldownSeconds > 0;

  const acceptStatus = useCallback((data) => {
    setStatus(data);
    // Use server-computed remaining time; a wrong phone clock cannot skip the wait.
    const seconds = Number(data?.cashback_cooldown?.remaining_seconds || 0);
    const now = performance.now();
    setClockNow(now);
    setCooldownEndsAt(data?.cashback_cooldown?.active && Number.isFinite(seconds) && seconds > 0
      ? now + seconds * 1000 : 0);
  }, []);

  const load = useCallback(async (silent = false) => {
    const version = ++requestVersion.current;
    if (!silent) setLoading(true);
    setRefreshing(silent);
    setRefreshFailed(false);
    try {
      const data = await fetchVipRewardsStatus({ tenantId, userId, preferCache: false });
      if (version === requestVersion.current) acceptStatus(data);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setRefreshFailed(silent);
      toast.error(error?.response?.data?.detail || 'Could not load VIP Rewards');
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [tenantId, userId, acceptStatus]);

  useEffect(() => {
    load();
    return () => { requestVersion.current += 1; };
  }, [load]);

  useEffect(() => {
    if (!cooldownEndsAt || claiming) return undefined;
    const timer = window.setInterval(() => {
      const now = performance.now();
      setClockNow(now);
      if (now >= cooldownEndsAt) {
        window.clearInterval(timer);
        setCooldownEndsAt(0);
        // Refresh once at expiry. The server, not the timer, makes a claim available.
        load(true);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldownEndsAt, load, claiming]);

  const recentClaimed = useMemo(
    () => claims.filter((claim) => claim.status === 'claimed').slice(0, 6),
    [claims],
  );

  const handleClaim = async (claimId) => {
    if (!claimId || claiming) return;
    const version = ++requestVersion.current;
    setClaiming(claimId);
    setRefreshing(false);
    setRefreshFailed(false);
    try {
      const data = await claimVipReward(claimId, cacheScope);
      if (version !== requestVersion.current) return;
      toast.success(`Claimed ${data.reward_label || 'VIP reward'}`);
      const nextStatus = data.status || await fetchVipRewardsStatus(cacheScope);
      if (version === requestVersion.current) acceptStatus(nextStatus);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Claim failed');
    } finally {
      setClaiming('');
    }
  };

  return (
    <div className="player-app min-h-screen pb-12 text-white" data-testid="vip-rewards-page">
      <div className="sticky top-0 z-30 flex items-center gap-3 bg-black px-4 py-4">
        <button
          type="button"
          onClick={() => navigate(explorePath)}
          className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/10 active:scale-95"
          aria-label="Back"
          data-testid="vip-rewards-back"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="display flex items-center gap-2 text-xl font-extrabold">
            <Sparkles className="h-6 w-6 text-[#FFD93D]" />
            {item.title || 'VIP Rewards'}
          </h1>
          <p className="text-xs text-white/70">{item.subtitle || 'Claim load bonuses and cashback rewards'}</p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl space-y-5 px-4 py-5">
        {loading ? (
          <div className="grid min-h-[360px] place-items-center rounded-3xl border border-white/10 bg-white/5">
            <Loader2 className="h-8 w-8 animate-spin text-[#FFD93D]" />
          </div>
        ) : !status?.enabled ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
            <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-white/45" />
            <p className="display text-lg font-extrabold">VIP Rewards are not active here.</p>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-[28px] border border-[#FFD93D]/35 bg-black/25 shadow-2xl shadow-black/35">
              <VipRewardsBanner
                key={JSON.stringify([tenantId, bannerUrl])}
                src={bannerUrl}
                title={item.title || 'VIP Rewards'}
                cashbackEnabled={cashbackEnabled}
                requiredDeposits={requiredDeposits}
                cashbackPercent={Number(cashback.cashback_percent ?? 5)}
              />
            </div>

            {availableClaims.length > 0 && (
              <div className="space-y-3">
                {availableClaims.map((claim) => (
                  <RewardClaimCard
                    key={claim.id}
                    claim={claim}
                    busy={claiming === claim.id}
                    onClaim={() => handleClaim(claim.id)}
                  />
                ))}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {freeplayEnabled && <InfoCard
                icon={Gift}
                title="Freeplay Load Bonus"
                body="Load from wallet and claim the matching FP reward once every 24 hours."
                accent="#22D3EE"
              >
                <div className="mt-3 grid grid-cols-2 gap-2 min-[390px]:grid-cols-3">
                  {tiers.map((tier, index) => (
                    <div key={`${tier.min}-${tier.max}-${index}`} className="min-w-0 rounded-2xl border border-white/10 bg-black/20 px-2 py-3 text-center">
                      <p className="break-words text-[10px] font-black uppercase tracking-[0.08em] text-white/45 min-[390px]:tracking-[0.12em]">
                        {formatMoney(tier.min)}{tier.max ? `-${formatMoney(tier.max)}` : '+'}
                      </p>
                      <p className="display mt-1 break-words text-sm font-black text-[#FFD93D]">{formatFp(tier.reward_fp)}</p>
                    </div>
                  ))}
                </div>
              </InfoCard>}

              {cashbackEnabled && <InfoCard
                icon={WalletCards}
                title="Cashback Streak"
                body={`Make ${requiredDeposits} successful wallet deposits without a game redeem and claim ${Number(cashback.cashback_percent ?? 5)}% cashback on those deposits.`}
                accent="#34D399"
              >
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs font-black uppercase tracking-[0.12em] text-white/50">
                    <span>Progress</span>
                    <span>{currentDeposits}/{requiredDeposits}</span>
                  </div>
                  <div className="mt-2 h-3 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-[#FFD93D]"
                      style={{ width: `${Math.min(100, (currentDeposits / Math.max(1, requiredDeposits)) * 100)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs font-semibold text-white/55">
                    Qualifying wallet deposit total: {formatMoney(progress.deposit_total || 0)}
                  </p>
                  <p className="mt-2 text-xs text-white/55">Any successful game redeem resets the count and total to zero. Loading a game does not count as a deposit.</p>
                  <p className="mt-2 text-xs text-white/55">Claiming clears the deposits used for that cashback. Extra or new deposits count toward your next claim.</p>
                  {waitingForCashback && (
                    <div className="mt-3 rounded-2xl border border-[#FFD93D]/30 bg-[#FFD93D]/10 p-3" data-testid="cashback-cooldown">
                      <p className="text-sm font-bold text-[#FDE68A]">
                        {currentDeposits >= requiredDeposits ? 'Next claim available in' : 'Cashback cooldown:'} {formatCountdown(cooldownSeconds)}
                      </p>
                      <p className="mt-1 text-xs text-white/70">
                        {currentDeposits >= requiredDeposits
                          ? 'Your deposit requirement is complete. The cooldown started when you last claimed cashback; a successful game redeem still resets this streak.'
                          : `The cooldown started when you last claimed cashback. Your next claim also requires ${requiredDeposits} new qualifying wallet deposits.`}
                      </p>
                    </div>
                  )}
                  {refreshing && <p className="mt-3 text-sm text-[#FDE68A]" role="status">Checking cashback eligibility…</p>}
                  {refreshFailed && (
                    <div className="mt-3 text-sm text-[#FDE68A]" role="status">
                      Could not refresh cashback eligibility.
                      <button type="button" onClick={() => load(true)} className="ml-2 underline">Retry</button>
                    </div>
                  )}
                </div>
              </InfoCard>}
            </div>

            {availableClaims.length === 0 && !waitingForCashback && !refreshing && !refreshFailed && (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-5 text-center">
                <Trophy className="mx-auto mb-2 h-8 w-8 text-[#FFD93D]" />
                <p className="display text-base font-extrabold">No VIP claim ready yet</p>
                <p className="mt-1 text-sm text-white/60">Complete a qualifying wallet load or cashback streak to unlock a claim.</p>
              </div>
            )}

            {recentClaimed.length > 0 && (
              <div className="rounded-3xl border border-white/10 bg-white/[0.035] p-4">
                <p className="display mb-3 text-sm font-extrabold text-white/85">Recent VIP Claims</p>
                <div className="space-y-2">
                  {recentClaimed.map((claim) => (
                    <div key={claim.id} className="flex min-w-0 items-center justify-between gap-3 rounded-2xl bg-black/20 px-3 py-2">
                      <span className="min-w-0 break-words text-sm font-bold text-white/75">{claim.title}</span>
                      <span className="max-w-[45%] shrink-0 truncate text-sm font-black text-[#FFD93D]" title={claimLabel(claim)}>{claimLabel(claim)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function VipRewardsBanner({ src, title, cashbackEnabled, requiredDeposits, cashbackPercent }) {
  const [failed, setFailed] = useState(false);
  const showImage = src && !failed;
  return <div className={`relative grid min-h-[168px] w-full min-w-0 bg-white/5 ${showImage ? 'aspect-[1600/760]' : 'sm:aspect-[1600/760]'}`}>
    {showImage ? <img
      src={src}
      alt={`${title} flyer`}
      className="absolute inset-0 h-full w-full object-contain"
      draggable={false}
      onError={() => setFailed(true)}
      data-testid="vip-rewards-banner"
    /> : cashbackEnabled ? (
      <div className="flex min-w-0 flex-col items-center justify-center gap-2 p-5 text-center" data-testid="vip-rewards-banner-fallback">
        <Trophy className="h-9 w-9 text-[#FFD93D]" />
        <p className="display text-xl font-black text-[#FFD93D]">VIP Rewards</p>
        <p className="font-bold">{requiredDeposits} wallet deposits · {cashbackPercent}% cashback</p>
        <p className="text-xs text-white/65">Any successful game redeem resets your streak.</p>
      </div>
    ) : (
      <div className="grid h-full place-items-center text-sm font-bold text-white/45" data-testid="vip-rewards-banner-fallback">VIP Rewards</div>
    )}
  </div>;
}

function RewardClaimCard({ claim, busy, onClaim }) {
  const Icon = claim.type === 'cashback' ? WalletCards : Gift;
  return (
    <div className="rounded-[28px] border border-[#FFD93D]/40 bg-[#FFD93D]/10 p-4 shadow-xl shadow-black/25">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 min-[360px]:flex min-[360px]:gap-4">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-b from-[#FFD93D] to-[#F59E0B] text-black shadow-lg shadow-[#FFD93D]/20">
          <Icon className="h-7 w-7" strokeWidth={2.7} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="display break-words text-base font-extrabold text-white">{claim.title || 'VIP Reward'}</p>
          <p className="break-words text-sm font-semibold text-[#FDE68A]">Available now: {claimLabel(claim)}</p>
        </div>
        <button
          type="button"
          onClick={onClaim}
          disabled={busy}
          className="col-span-2 grid h-12 w-full place-items-center rounded-2xl bg-[#FFD93D] px-4 text-sm font-black text-black active:scale-95 disabled:opacity-70 min-[360px]:col-auto min-[360px]:w-auto min-[360px]:min-w-[92px]"
          data-testid={`vip-claim-${claim.id}`}
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Claim'}
        </button>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, body, accent, children }) {
  return (
    <div
      className="rounded-[24px] border p-4"
      style={{
        borderColor: `${accent}66`,
        background: `linear-gradient(135deg, ${accent}22, rgba(255,255,255,0.04))`,
      }}
    >
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl" style={{ background: `${accent}22`, color: accent }}>
          <Icon className="h-5 w-5" strokeWidth={2.5} />
        </span>
        <div>
          <p className="display text-base font-extrabold text-white">{title}</p>
          <p className="mt-1 text-sm font-semibold leading-relaxed text-white/62">{body}</p>
        </div>
      </div>
      {children}
    </div>
  );
}
